import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const EducationalContent = () => {
  const [expandedSection, setExpandedSection] = useState(null);

  const educationalSections = [
    {
      id: 'corpus',
      icon: 'Wallet',
      title: 'Understanding Retirement Corpus',
      content: 'A retirement corpus is the total amount of money you need to accumulate by retirement to maintain your desired lifestyle. In India, factors like inflation (typically 6-7% annually), healthcare costs, and life expectancy (75-80 years) significantly impact corpus calculations. A general rule suggests having 25-30 times your annual expenses as retirement corpus.'
    },
    {
      id: 'inflation',
      icon: 'TrendingUp',
      title: 'Impact of Inflation',
      content: 'Inflation erodes purchasing power over time. In India, historical inflation averages 6-7% annually. This means ₹1 lakh today will need ₹2 lakhs in 12 years to maintain the same purchasing power. Pre-retirement and post-retirement inflation rates may differ, affecting your corpus calculations and withdrawal strategies.'
    },
    {
      id: 'returns',
      icon: 'PieChart',
      title: 'Expected Investment Returns',
      content: 'Indian retirement portfolios typically include PPF (7-8%), EPF (8-8.5%), mutual funds (10-12%), and NPS (9-10%). A balanced portfolio might expect 9-11% annual returns. Conservative estimates help ensure your corpus lasts throughout retirement. Consider tax-efficient instruments like NPS for additional benefits under Section 80CCD.'
    },
    {
      id: 'savings',
      icon: 'PiggyBank',
      title: 'Optimal Savings Rate',
      content: 'Financial experts recommend saving 15-20% of income for retirement. Starting early leverages compound interest significantly. For example, saving ₹10,000 monthly from age 30 at 10% returns yields ₹2.28 crores by age 60, while starting at 40 yields only ₹75 lakhs. The power of compounding makes early starts crucial.'
    }
  ];

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
          <Icon name="BookOpen" size={20} className="text-primary" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Retirement Planning Guide</h3>
          <p className="text-sm text-muted-foreground">Essential concepts for Indian retirement planning</p>
        </div>
      </div>
      <div className="space-y-3">
        {educationalSections?.map((section) => (
          <div key={section?.id} className="border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => toggleSection(section?.id)}
              className="w-full flex items-center justify-between p-4 bg-muted/30 hover:bg-muted/50 transition-colors duration-200"
              aria-expanded={expandedSection === section?.id}
            >
              <div className="flex items-center gap-3">
                <Icon name={section?.icon} size={20} className="text-primary" />
                <span className="text-sm font-medium text-foreground">{section?.title}</span>
              </div>
              <Icon 
                name={expandedSection === section?.id ? 'ChevronUp' : 'ChevronDown'} 
                size={20} 
                className="text-muted-foreground" 
              />
            </button>
            {expandedSection === section?.id && (
              <div className="p-4 bg-card animate-fade-in">
                <p className="text-sm text-muted-foreground leading-relaxed">{section?.content}</p>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 p-4 bg-accent/5 border border-accent/20 rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Lightbulb" size={20} className="text-accent flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-2">Pro Tip</h4>
            <p className="text-xs text-muted-foreground">
              Review and adjust your retirement plan annually. Life changes, market conditions, and inflation rates evolve. Regular reviews ensure your plan stays aligned with your goals and circumstances.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EducationalContent;